<template>
  <BaseModal :show="show" :show-close="true" v-bind="$attrs" @close="handleClose" @closed="handleClosed">
    <div class="login-modal-content">
      <!-- Matching animation section (使用Figma切图) -->
      <div class="matching-section">
        <img src="@/assets/images/components/login-modal/match.png" alt="match" />
      </div>

      <!-- Login buttons -->
      <div class="login-buttons">
        <button class="login-btn google-btn" @click="handleGoogleLogin">
          <div id="google-login-btn"></div>
          <img src="@/assets/images/icons/google.svg" alt="Google" class="btn-icon" />
          <span>{{ $t('login.signInWithGoogle') }}</span>
        </button>

        <button id="apple-login-btn" class="login-btn apple-btn" @click="handleAppleLogin">
          <img src="@/assets/images/icons/apple.svg" alt="Apple" class="btn-icon" />
          <span>{{ $t('login.continueWithApple') }}</span>
        </button>

        <!-- 手机号登录按钮（仅测试环境） -->
        <button v-if="__DEV__" class="login-btn phone-btn" @click="handlePhoneLoginClick">
          <img src="@/assets/images/icons/phone.svg" alt="Phone" class="btn-icon" />
          <span>Phone Login (Test Only)</span>
        </button>
      </div>

      <!-- Terms and conditions -->
      <div class="terms-section" ref="terms-section" :class="{ 'shake': isShaking }">
        <label class="checkbox-wrapper">
          <input type="checkbox" v-model="agreedToTerms" class="checkbox-input" />
          <!-- 使用 Figma 切图的 checkbox -->
          <img v-if="agreedToTerms" src="@/assets/images/icons/checkbox_active.svg" alt="checkbox checked"
            class="checkbox-custom" />
          <img v-else src="@/assets/images/icons/checkbox.svg" alt="checkbox unchecked" class="checkbox-custom" />
          <i18n-t keypath="login.termsText" tag="span" class="terms-text">
            <template #terms>
              <a href="#" @click.prevent="openTerms">{{ $t('login.termsOfService') }}</a>
            </template>
            <template #privacy>
              <a href="#" @click.prevent="openPrivacy">{{ $t('login.privacyPolicy') }}</a>
            </template>
          </i18n-t>
        </label>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { ref, onMounted, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { showToast, showLoadingToast, closeToast } from 'vant'
import { useRouter } from 'vue-router'
import BaseModal from '../BaseModal.vue'
import { useEventListener } from '@vueuse/core'
import { isWebView } from '@/utils/device'
import googleAuthService from '@/services/googleAuth'
import appleAuthService from '@/services/appleAuth'
import { delay as _delay } from 'lodash'
import { useGlobalStore } from '@/stores/global'

const { t } = useI18n()

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  source: {
    type: String,
    default: 'unknown'
  },
  redirect: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['closed', 'update:show', 'close'])

const router = useRouter()
const userStore = useUserStore()
const globalStore = useGlobalStore()
const agreedToTerms = ref(false)
const isShaking = ref(false) // 控制抖动动画
const termsRef = useTemplateRef('terms-section') // terms-section 的引用

/** 手机号登陆 START: 仅在开发/测试环境显示 */
const __DEV__ = import.meta.env.MODE !== 'production'

/**
 * 处理手机号登录按钮点击
 * 使用动态导入避免在生产环境打包 PhoneLoginModal
 */
async function handlePhoneLoginClick() {
  if (!globalStore.isActiveTab) return
  if (!checkAgreement()) return

  emit('update:show', false) // 关闭登录弹窗

  // 动态导入 PhoneLoginModal（生产环境会被 tree-shaking 移除）
  if (__DEV__) {
    try {
      const { showPhoneLoginModal } = await import('@/components/PhoneLoginModal')
      showPhoneLoginModal({
        redirect: props.redirect,
      })
    } catch (error) {
      console.error('[LoginModal] Failed to load PhoneLoginModal:', error)
    }
  }
}
/** 手机号登陆 END */

/**
 * 组件挂载时预加载 Google 和 Apple SDK
 */
onMounted(async () => {
  // 如果是在 WebView 环境或已登录，则不预加载 SDK
  if (isWebView() || userStore.isLoggedIn) return

  // 预加载 Google 和 Apple SDK
  try {
    await Promise.all([appleAuthService.initialize(), googleAuthService.initialize()])
    console.log('[LoginModal] Google and Apple SDK initialized successfully')
  } catch (error) {
    console.error('[LoginModal] Failed to initialize Google and Apple SDK:', error)
  }
})

// 监听动画结束事件
useEventListener(termsRef, 'animationend', () => isShaking.value = false)

/**
 * 检查是否已同意服务条款
 * @returns {boolean} 是否通过检查
 */
function checkAgreement() {
  if (!agreedToTerms.value) {
    isShaking.value = true
    return false
  }
  return true
}

function checkSupport() {
  if (isWebView()) {
    showToast(t('login.webviewNotSupported'))
    return false
  }
  return true
}

/**
 * 处理 Google 登录
 */
async function handleGoogleLogin() {
  if (!globalStore.isActiveTab) return
  if (!checkSupport()) return
  // 1. 检查服务条款是否已同意
  if (!checkAgreement()) return

  try {
    // 2. 触发 Google 登录弹窗
    showLoadingToast({
      message: t('login.authenticating'),
      forbidClick: true,
      duration: 0,
    })
    const authResult = await googleAuthService.login()
    if (authResult?.error) {
      throw new Error(authResult.error)
    }
    console.log('[LoginModal] Google login successful, authResult:', authResult)
    showLoadingToast({
      message: t('login.processing'),
      forbidClick: true,
      duration: 0,
    })
    // 3. 调用后端接口进行认证
    const result = await userStore.loginWithGoogle({
      ...authResult,
      redirect_uri: window.location.origin,
    })
    if (result) {
      // const result = {
      //   action: userStore.ACTION_TYPE.REGISTER
      // }
      userStore.fetchUserInfo({ hideLoading: true })
      userStore.fetchMemberAsset({ hideLoading: true })

      // 4. 根据返回的 action 决定下一步
      console.log('[LoginModal] Google login successful, result:', result)
      showToast(t('login.success'))
      emit('update:show', false)

      // 如果有 redirect 参数，跳转到指定页面
      if (props.redirect) {
        router.push(props.redirect)
      }
    } else {
      closeToast()
    }
  } catch (error) {
    console.error('[LoginModal] Google login error:', error)
    showToast(error.message || t('login.failed'))
  }
}

/**
 * 处理 Apple 登录
 */
async function handleAppleLogin() {
  if (!globalStore.isActiveTab) return
  if (!checkSupport()) return
  // 1. 检查服务条款是否已同意
  if (!checkAgreement()) return
  // 2. 触发 Apple 登录（弹窗模式）
  showLoadingToast({
    message: t('login.authenticating'),
    forbidClick: true,
    duration: 0,
  })

  /**
   * 在 Mac Safari 上，使用 JS SDK 弹窗方式登录
   * 需要延迟 10ms 左右后再触发登录，否则 loading 动画不会出现
   */
  _delay(async () => {
    try {
      const authData = await appleAuthService.login()

      if (authData?.error) {
        throw new Error(authData.error)
      }

      console.log('[LoginModal] Apple login successful, authData:', authData)
      showLoadingToast({
        message: t('login.processing'),
        forbidClick: true,
        duration: 0,
      })

      // 3. 调用后端接口进行认证
      const result = await userStore.loginWithApple(authData)
      if (result) {
        userStore.fetchUserInfo({ hideLoading: true })
        userStore.fetchMemberAsset({ hideLoading: true })
        // 4. 根据返回的 action 决定下一步
        console.log('[LoginModal] Apple login successful, result:', result)
        showToast(t('login.success'))
        emit('update:show', false)

        // 如果有 redirect 参数，跳转到指定页面
        if (props.redirect) {
          router.push(props.redirect)
        }
      } else {
        closeToast()
      }
    } catch (error) {
      console.error('[LoginModal] Apple login error:', error)
      showToast(error.message || t('login.failed'))
    }
  }, 10)
}

function openTerms() {
  emit('update:show', false)
  // Open terms of service
  router.push('/terms-of-use')
}

function openPrivacy() {
  emit('update:show', false)
  // Open privacy policy
  router.push('/privacy-policy')
}

function handleClose() {
  emit('update:show', false)
  emit('close')
}

function handleClosed() {
  emit('closed')
}
</script>

<style scoped lang="less">
#google-login-btn {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.login-modal-content {
  position: relative;

  /**Matching animation section (使用 Figma 切图) */
  .matching-section {
    position: relative;
    height: 217px;
    width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    text-align: center;

    img {
      width: 202px;
      height: 194px;
    }
  }

  /** Login buttons */
  .login-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;

    .login-btn {
      width: 100%;
      height: 48px;
      position: relative;
      border-radius: 41px;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      cursor: pointer;
      transition: all 0.3s ease;

      &.google-btn {
        background: linear-gradient(141deg, #C764FF 3.02%, #7F4AFF 96.08%);
        color: #ffffff;
      }

      &.apple-btn {
        background: #f2f6ff;
        color: #000000;

        // &:hover {
        //   background: #e5edff;
        // }
      }

      &.phone-btn {
        background: #fff3cd;
        color: #856404;
        border: 1px dashed #856404;

        // &:hover {
        //   background: #ffe69c;
        // }
      }

      .btn-icon {
        width: 24px;
        height: 24px;
      }

      &:active {
        transform: scale(0.98);
      }
    }
  }

  /** Terms section */
  .terms-section {
    margin-left: -12px;

    &.shake {
      animation: shake 0.5s;
    }

    .checkbox-wrapper {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      cursor: pointer;

      .checkbox-input {
        display: none;
      }

      .checkbox-custom {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        position: relative;
        margin-top: 2px;
        cursor: pointer;
      }

      .terms-text {
        padding-right: 8px;
        font-size: 12px;
        line-height: 14px;
        color: #A5A5B5;
        text-align: justify;

        a {
          color: #7f4aff;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}

/** 抖动动画 */
@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-8px);
  }

  20%,
  40%,
  60%,
  80% {
    transform: translateX(8px);
  }
}
</style>
